/**
 * The camera input (docs/scanner.md §2): the platform's BarcodeDetector where
 * it exists (Android Chrome), otherwise the zxing-wasm polyfill (iOS Safari),
 * whose wasm is served from this origin and precached so it works offline.
 * Continuous detection with a per-code cooldown; the caller decides.
 */

type Detector = { detect(source: ImageBitmapSource): Promise<{ rawValue: string }[]> };

let detectorPromise: Promise<Detector> | null = null;

async function loadDetector(): Promise<Detector> {
  const native = (globalThis as { BarcodeDetector?: new (options: { formats: string[] }) => Detector }).BarcodeDetector;
  if (native) {
    try {
      return new native({ formats: ['qr_code'] });
    } catch {
      // fall through to the polyfill
    }
  }
  const polyfill = await import('barcode-detector/ponyfill');
  polyfill.prepareZXingModule({ overrides: { locateFile: (path: string, prefix: string) => (path.endsWith('.wasm') ? `/zxing/${path}` : `${prefix}${path}`) } });
  return new polyfill.BarcodeDetector({ formats: ['qr_code'] });
}

export function getDetector(): Promise<Detector> {
  detectorPromise ??= loadDetector();
  return detectorPromise;
}

export interface CameraSession {
  stop(): void;
}

/** Starts the rear camera into the video element and calls onCode for each decoded QR (the same code at most once per cooldown). */
export async function startCamera(video: HTMLVideoElement, onCode: (raw: string) => void, options: { cooldownMs?: number } = {}): Promise<CameraSession> {
  const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false });
  video.srcObject = stream;
  video.setAttribute('playsinline', 'true');
  await video.play();
  const detector = await getDetector();
  const cooldown = options.cooldownMs ?? 2000;
  const lastSeen = new Map<string, number>();
  let running = true;
  let busy = false;
  const tick = async () => {
    if (!running) return;
    if (!busy && video.readyState >= 2) {
      busy = true;
      try {
        const codes = await detector.detect(video);
        const now = Date.now();
        for (const code of codes) {
          const value = code.rawValue?.trim();
          if (!value) continue;
          const seen = lastSeen.get(value);
          if (seen && now - seen < cooldown) continue;
          lastSeen.set(value, now);
          onCode(value);
        }
      } catch {
        // a frame that could not be decoded; try the next
      } finally {
        busy = false;
      }
    }
    if (running) setTimeout(() => void tick(), 120);
  };
  void tick();
  return {
    stop() {
      running = false;
      for (const track of stream.getTracks()) track.stop();
      video.srcObject = null;
    },
  };
}

/** Result feedback that is never color alone (spec §67): a tone and a vibration pattern per outcome. */
export function feedback(color: 'GREEN' | 'YELLOW' | 'RED'): void {
  const patterns: Record<string, number[]> = { GREEN: [80], YELLOW: [120, 80, 120], RED: [300, 100, 300, 100, 300] };
  try {
    navigator.vibrate?.(patterns[color] ?? [80]);
  } catch {
    // not supported
  }
  try {
    const AudioContextCtor = (globalThis as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext ?? (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextCtor) return;
    const ctx = ((globalThis as { __breAudio?: AudioContext }).__breAudio ??= new AudioContextCtor());
    const tones: Record<string, [number, number][]> = { GREEN: [[1046, 0.12]], YELLOW: [[660, 0.12], [660, 0.12]], RED: [[220, 0.25], [180, 0.25]] };
    let at = ctx.currentTime;
    for (const [frequency, duration] of tones[color] ?? tones.GREEN!) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = frequency;
      osc.type = 'square';
      gain.gain.setValueAtTime(0.15, at);
      gain.gain.exponentialRampToValueAtTime(0.001, at + duration);
      osc.connect(gain).connect(ctx.destination);
      osc.start(at);
      osc.stop(at + duration);
      at += duration + 0.06;
    }
  } catch {
    // no audio
  }
}

export async function keepAwake(): Promise<() => void> {
  try {
    const lock = await (navigator as { wakeLock?: { request(type: 'screen'): Promise<{ release(): Promise<void> }> } }).wakeLock?.request('screen');
    return () => void lock?.release().catch(() => undefined);
  } catch {
    return () => undefined;
  }
}
